import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/SectionHeading";
import { EMICalculator } from "../components/EMICalculator";
import { EligibilityChecker } from "../components/EligibilityChecker";
import { useLead } from "../context/LeadContext";
import { useAuth } from "../lib/auth";
import { findSegment, SEGMENTS, SERVICES, waLink, telLink } from "../lib/data";
import { themeFor } from "../lib/segmentTheme";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

export default function SegmentPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openLead } = useLead();
  const { user } = useAuth();
  const seg = findSegment(slug);
  const th = themeFor(slug);

  useEffect(() => { window.scrollTo(0, 0); if (!seg) navigate("/businesses"); }, [slug]);
  if (!seg) return null;

  const handleCta = (action) => {
    if (action === "loan") openLead({ source: `segment-${slug}`, loan_type: "Business Loan" });
    else if (action === "franchise") navigate("/franchise");
    else if (action === "investor") navigate("/investors");
    else if (action === "internship" || action === "career") navigate("/careers");
    else if (action === "login") navigate(user ? "/dashboard" : "/login");
    else if (action === "register") navigate(user ? "/dashboard" : "/register");
    else openLead({ source: `segment-${slug}` });
  };

  const others = SEGMENTS.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="segment-page" className="pt-20 bg-cream-100 text-soil-700 min-h-screen">
      {/* HERO */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img loading="lazy" decoding="async" src={seg.img} alt={seg.name} className="w-full h-full object-cover opacity-35" />
          <div className={`absolute inset-0 bg-gradient-to-br ${th.grad}`} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <Link to="/businesses" className="inline-flex items-center gap-1.5 text-soil-600 hover:text-terracotta-600 text-sm mb-6" data-testid="back-to-businesses"><Icons.ArrowLeft className="w-4 h-4" /> All Businesses</Link>
          {seg.star && <div className="inline-block ml-3 text-[10px] font-bold px-2 py-1 rounded-full forest-gradient text-cream-50">★ STAR VERTICAL</div>}
          <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center mb-5 ${th.icon}`}><Icon name={seg.icon} className="w-7 h-7" /></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-forest-800 max-w-3xl">{seg.name}</h1>
          <p className={`font-medium mt-2 text-lg ${th.ring}`}>{seg.tagline}</p>
          <p className="text-soil-600 mt-4 max-w-2xl text-lg">{seg.intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {seg.ctas.map((c, i) => (
              <Button key={c.label} onClick={() => handleCta(c.action)} data-testid={`segment-cta-${i}`}
                className={i === 0 ? `${th.btn} font-bold h-12 px-7 rounded-full` : "bg-forest-100 border border-cream-300 text-forest-800 font-bold h-12 px-6 rounded-full hover:bg-white/20"}>
                {c.label}
              </Button>
            ))}
            <a href={waLink(`Hi, I'm interested in ${seg.name}`)} target="_blank" rel="noreferrer nofollow"><Button className="bg-approval hover:bg-approval-dark text-forest-800 font-bold h-12 px-6 rounded-full"><Icons.MessageCircle className="w-5 h-5 mr-2" /> WhatsApp</Button></a>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-14 border-b border-cream-300 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-3 gap-6">
          {seg.highlights.map((h) => (<div key={h.v} className="text-center"><div className={`font-mono-fin text-3xl font-extrabold ${th.ring}`}>{h.k}</div><div className="text-sm text-soil-500 mt-1">{h.v}</div></div>))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl font-bold text-forest-800 mb-6">What We Offer</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {seg.features.map((f) => (<div key={f} className="card-cream rounded-xl p-5 flex items-start gap-3"><Icons.CheckCircle2 className="w-5 h-5 text-approval shrink-0 mt-0.5" /><span className="text-soil-700">{f}</span></div>))}
          </div>
        </div>
      </section>

      {/* PRODUCTS (agro star) */}
      {seg.products && (
        <section className="py-16 bg-cream-200 border-y border-cream-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionHeading overline="Our Products" title="Eco Products That Sell" subtitle="Premium, natural and export-ready — crafted responsibly." center={false} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {seg.products.map((p) => (
                <div key={p.name} className="group card-cream rounded-2xl overflow-hidden hover:border-forest-500/40 transition-all">
                  <div className="h-44 overflow-hidden"><img loading="lazy" decoding="async" src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /></div>
                  <div className="p-5"><h3 className="font-heading font-semibold text-forest-800">{p.name}</h3><p className="text-soil-500 text-sm mt-1">{p.desc}</p></div>
                </div>
              ))}
            </div>
            {seg.models && (
              <div className="mt-10">
                <h3 className="font-heading text-xl font-bold text-forest-800 mb-4">Business Models</h3>
                <div className="flex flex-wrap gap-3">{seg.models.map((m) => (<span key={m} className="px-4 py-2 rounded-full bg-gold/12 border border-saffron-500/30 text-terracotta-600 text-sm">{m}</span>))}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FINANCE tools + sub-services */}
      {seg.isFinance && (
        <>
          <section className="py-16 bg-cream-200 border-y border-cream-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <SectionHeading overline="Loan Doctor Tools" title="Plan & Check Instantly" />
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div><h3 className="font-heading text-xl font-bold text-forest-800 mb-4 flex items-center gap-2"><Icons.Calculator className="w-5 h-5 text-terracotta-600" /> EMI Calculator</h3><EMICalculator /></div>
                <div><h3 className="font-heading text-xl font-bold text-forest-800 mb-4 flex items-center gap-2"><Icons.Gauge className="w-5 h-5 text-terracotta-600" /> Eligibility Checker</h3><EligibilityChecker /></div>
              </div>
            </div>
          </section>
          <section className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <SectionHeading overline="Finance Services" title="Loan Doctor Service Range" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="group card-cream rounded-2xl p-6 hover:border-forest-500/40 transition-all hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-xl bg-gold/12 border border-saffron-500/30 flex items-center justify-center mb-3"><Icon name={s.icon} className="w-5 h-5 text-terracotta-600" /></div>
                    <h3 className="font-heading font-semibold text-forest-800 group-hover:text-terracotta-600 transition-colors">{s.title}</h3>
                    <p className="text-soil-500 text-sm mt-1 line-clamp-2">{s.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* OTHER BUSINESSES */}
      <section className="py-16 bg-cream-200 border-t border-cream-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl font-bold text-forest-800 mb-8">Explore More Genesis Businesses</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/businesses/${s.slug}`} className="group card-cream rounded-2xl p-6 hover:border-forest-500/40 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gold/12 border border-saffron-500/30 flex items-center justify-center mb-3"><Icon name={s.icon} className="w-5 h-5 text-terracotta-600" /></div>
                <h3 className="font-heading font-semibold text-forest-800 group-hover:text-terracotta-600 transition-colors">{s.short}</h3>
                <p className="text-soil-500 text-sm mt-1 line-clamp-2">{s.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
